import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import { Dialog } from '../design-system';
import { supabase } from '../lib/supabase';
import { getListById, articles$, getIsArchived, isArticleInList } from '../store';
import { bulkGetContentStrings } from '../lib/contentCache';
import { MS_PER_DAY } from '../constants/time';
import './PredictionModal.css';

/**
 * Linear regression using Ordinary Least Squares (OLS)
 * Returns slope, intercept, and predict function
 */
const linearRegression = (points) => {
  if (points.length === 0) {
    return { slope: 0, intercept: 0, predict: () => 0, r2: 0 };
  }
  if (points.length === 1) {
    // With only one point, use a reasonable default slope for prediction
    return { slope: 1, intercept: points[0].y - points[0].x, predict: (x) => x, r2: 1 };
  }

  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);

  const denominator = n * sumX2 - sumX * sumX;
  if (denominator === 0) {
    const avgY = sumY / n;
    return { slope: 0, intercept: avgY, predict: () => avgY, r2: 0 };
  }

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  // Calculate R² (coefficient of determination)
  const meanY = sumY / n;
  const ssTotal = points.reduce((s, p) => s + (p.y - meanY) ** 2, 0);
  const ssResidual = points.reduce((s, p) => s + (p.y - (slope * p.x + intercept)) ** 2, 0);
  const r2 = ssTotal === 0 ? 1 : 1 - ssResidual / ssTotal;

  return {
    slope,
    intercept,
    predict: (x) => Math.max(0, slope * x + intercept),
    r2
  };
};

/**
 * Count blocks in content (markdown blocks)
 */
const countBlocks = (content) => {
  if (!content) return 0;
  const blocks = content.split(/\n\n+/).filter(block => block.trim());
  return blocks.length || 1;
};

/**
 * Generate smooth bezier curve path from points
 * Uses Catmull-Rom to Bezier conversion for smooth curves
 */
const generateSmoothPath = (points, width, height, maxX, maxY) => {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const x = (points[0].x / maxX) * width;
    const y = height - (points[0].y / maxY) * height;
    return `M ${x} ${y} L ${x} ${y}`;
  }

  // Scale points to SVG coordinates
  const scaled = points.map(p => ({
    x: (p.x / maxX) * width,
    y: height - (p.y / maxY) * height
  }));

  // Start path
  let path = `M ${scaled[0].x} ${scaled[0].y}`;

  // Use quadratic bezier for smoothing
  for (let i = 1; i < scaled.length; i++) {
    const prev = scaled[i - 1];
    const curr = scaled[i];

    // Simple smooth curve using control point at midpoint
    const cpX = (prev.x + curr.x) / 2;
    const cpY = (prev.y + curr.y) / 2;

    if (i === 1) {
      // First segment: straight to midpoint, then curve
      path += ` L ${cpX} ${cpY}`;
    }

    if (i < scaled.length - 1) {
      const next = scaled[i + 1];
      const nextCpX = (curr.x + next.x) / 2;
      const nextCpY = (curr.y + next.y) / 2;
      path += ` Q ${curr.x} ${curr.y} ${nextCpX} ${nextCpY}`;
    } else {
      // Last segment
      path += ` Q ${curr.x} ${curr.y} ${curr.x} ${curr.y}`;
    }
  }

  return path;
};

/**
 * Generate area path (for gradient fill under curve)
 */
const generateAreaPath = (points, width, height, maxX, maxY) => {
  if (points.length === 0) return '';

  const linePath = generateSmoothPath(points, width, height, maxX, maxY);
  if (!linePath) return '';

  // Get the last point's x coordinate
  const lastX = (points[points.length - 1].x / maxX) * width;
  const firstX = (points[0].x / maxX) * width;

  // Close the path to form an area
  return `${linePath} L ${lastX} ${height} L ${firstX} ${height} Z`;
};

const PredictionModal = ({ isOpen, onClose, listId }) => {
  const { t } = useTranslation('reader');
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState([]);
  const [scopeHistory, setScopeHistory] = useState({ timeline: [], total: 0 });
  const [hoverInfo, setHoverInfo] = useState(null); // { dayIndex, x, scopeValue, completedValue, date }

  // Get list info from store
  const list = useSelector(() => listId && listId !== 'all' ? getListById(listId) : null);
  const listName = list?.name || t('prediction.allArticles');

  // SVG dimensions
  const svgWidth = 460;
  const svgHeight = 200;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartWidth = svgWidth - padding.left - padding.right;
  const chartHeight = svgHeight - padding.top - padding.bottom;

  // Fetch data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Get list created_at from store (inside effect to avoid dep issues)
        const currentList = listId && listId !== 'all' ? getListById(listId) : null;
        const listCreatedAt = currentList?.created_at ? new Date(currentList.created_at) : null;

        // Determine time range - use list creation date or default to 18 weeks
        const startDate = listCreatedAt || new Date(Date.now() - 18 * 7 * MS_PER_DAY);

        // Fetch activity data for this list
        let query = supabase
          .from('reedle_reading_activity')
          .select('hour_bucket, block_count, article_id')
          .gte('hour_bucket', startDate.toISOString())
          .order('hour_bucket', { ascending: true });

        // If filtering by specific list, use junction table for multi-list support
        if (listId && listId !== 'all') {
          const { data: junctionRows } = await supabase
            .from('reedle_article_lists')
            .select('article_id')
            .eq('list_id', listId);

          const articleIds = (junctionRows || []).map(r => r.article_id);

          if (articleIds.length === 0) {
            setActivityData([]);
            setScopeHistory({ timeline: [], total: 0, archivedBlocks: [] });
            setLoading(false);
            return;
          }

          query = supabase
            .from('reedle_reading_activity')
            .select('hour_bucket, block_count, article_id')
            .gte('hour_bucket', startDate.toISOString())
            .in('article_id', articleIds)
            .order('hour_bucket', { ascending: true });
        }

        const { data: activity, error } = await query;
        if (error) throw error;

        setActivityData(activity || []);

        // Calculate scope (total blocks) from local store + contentCache
        // Also track archived articles - their blocks count as "completed"
        const articlesObj = articles$.get() || {};
        let totalScope = 0;
        const scopePoints = [];
        const archivedBlocks = []; // Track archived article blocks with their archive/update time

        // Determine if we're showing "all" articles or a specific list
        const isAllArticles = !listId || listId === 'all';

        // Filter relevant articles first
        const relevantArticles = Object.values(articlesObj).filter(article => {
          if (!article || article.deleted) return false;
          if (!isAllArticles && !isArticleInList(article.id, listId)) return false;
          return true;
        });

        // Bulk-load content strings from contentCache (single IDB transaction)
        const contentMap = await bulkGetContentStrings(relevantArticles.map(a => a.id));

        relevantArticles.forEach(article => {
          const content = contentMap.get(article.id) || '';
          const blocks = countBlocks(content);
          totalScope += blocks;

          // Track when articles were added for scope history
          if (article.created_at) {
            scopePoints.push({
              date: new Date(article.created_at),
              blocks
            });
          }

          // For archived articles (per-user), treat all blocks as "completed"
          // Use updated_at as approximate archive time, or created_at as fallback
          if (getIsArchived(article.id) && blocks > 0) {
            const archiveDate = article.updated_at ? new Date(article.updated_at) : new Date(article.created_at);
            archivedBlocks.push({
              date: archiveDate,
              blocks,
              articleId: article.id
            });
          }
        });

        // Sort and accumulate scope over time
        scopePoints.sort((a, b) => a.date - b.date);
        let cumScope = 0;
        const scopeTimeline = scopePoints.map(p => {
          cumScope += p.blocks;
          return { date: p.date, cumulative: cumScope, blocks: p.blocks };
        });

        setScopeHistory({ timeline: scopeTimeline, total: totalScope, archivedBlocks });
      } catch (err) {
        console.error('Failed to fetch prediction data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, listId]);

  // Process data for chart
  const chartData = useMemo(() => {
    if (loading || scopeHistory.total === undefined) {
      return { completed: [], scope: [], prediction: [], maxX: 1, maxY: 1 };
    }

    // Get list created_at for date range calculation
    const currentList = listId && listId !== 'all' ? getListById(listId) : null;
    const listCreatedAt = currentList?.created_at ? new Date(currentList.created_at) : null;
    const startDate = listCreatedAt || new Date(Date.now() - 18 * 7 * MS_PER_DAY);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const totalDays = Math.ceil((today - startDate) / MS_PER_DAY);
    const predictionDays = Math.max(7, Math.ceil(totalDays * 0.3));

    // Aggregate completed blocks by day from activity data
    // But exclude archived articles - we'll add them separately as 100% complete
    const archivedArticleIds = new Set((scopeHistory.archivedBlocks || []).map(a => a.articleId));

    const dailyMap = {};
    activityData.forEach(row => {
      // Skip activity from archived articles - we count them as 100% complete separately
      if (archivedArticleIds.has(row.article_id)) return;

      const date = new Date(row.hour_bucket);
      const dayNum = Math.floor((date - startDate) / MS_PER_DAY);
      if (dayNum >= 0) {
        dailyMap[dayNum] = (dailyMap[dayNum] || 0) + (row.block_count || 0);
      }
    });

    // Add archived article blocks as completed (treat ALL their blocks as read)
    // Add them on the day they were archived (or updated)
    (scopeHistory.archivedBlocks || []).forEach(archived => {
      const dayNum = Math.floor((archived.date - startDate) / MS_PER_DAY);
      const clampedDay = Math.max(0, Math.min(dayNum, totalDays));
      dailyMap[clampedDay] = (dailyMap[clampedDay] || 0) + archived.blocks;
    });

    // Build cumulative completed curve
    let cumulative = 0;
    const completed = [];
    for (let d = 0; d <= totalDays; d++) {
      cumulative += dailyMap[d] || 0;
      completed.push({ x: d, y: cumulative });
    }

    // Build scope curve (cumulative total blocks over time as articles were added)
    const scope = [];
    const scopeByDay = {};

    // Map each article's blocks to the day it was added
    (scopeHistory.timeline || []).forEach(point => {
      const dayNum = Math.floor((point.date - startDate) / MS_PER_DAY);
      // Clamp to day 0 if article was created before startDate
      const clampedDay = Math.max(0, dayNum);
      if (clampedDay <= totalDays) {
        scopeByDay[clampedDay] = (scopeByDay[clampedDay] || 0) + (point.blocks || 0);
      }
    });

    // Fill scope curve - accumulate blocks as articles were added
    let runningScope = 0;
    for (let d = 0; d <= totalDays + predictionDays; d++) {
      if (d <= totalDays && scopeByDay[d]) {
        runningScope += scopeByDay[d];
      }
      // After today (in prediction zone), scope stays at final total
      const scopeValue = d > totalDays ? scopeHistory.total : runningScope;
      scope.push({ x: d, y: scopeValue });
    }

    // Ensure scope ends at total by today (articles added before list creation go to day 0)
    // If there's a gap between runningScope and total, it means some articles
    // were created before startDate - attribute those to day 0
    if (scope.length > 0) {
      const finalActualScope = scope.find(p => p.x === totalDays)?.y || runningScope;
      if (finalActualScope < scopeHistory.total) {
        const missingScope = scopeHistory.total - finalActualScope;
        // Add missing scope to day 0 (articles created before tracking started)
        for (let i = 0; i < scope.length && scope[i].x <= totalDays; i++) {
          scope[i].y += missingScope;
        }
      }
    }

    // Linear regression on completed curve for prediction
    const regressionPoints = completed.filter(p => p.y > 0).map(p => ({ x: p.x, y: p.y }));
    const regression = linearRegression(regressionPoints);

    // Generate prediction curve (dashed extension)
    const prediction = [];
    const lastCompleted = completed[completed.length - 1];

    if (lastCompleted && regression.slope > 0) {
      for (let d = totalDays; d <= totalDays + predictionDays; d++) {
        const predicted = regression.predict(d);
        prediction.push({ x: d, y: Math.min(predicted, scopeHistory.total * 1.1) });
      }
    }

    // Calculate intersection (estimated completion)
    let completionDay = null;
    if (regression.slope > 0 && scopeHistory.total > lastCompleted?.y) {
      completionDay = Math.ceil((scopeHistory.total - regression.intercept) / regression.slope);
    }

    const maxX = totalDays + predictionDays;
    const maxY = Math.max(scopeHistory.total, lastCompleted?.y || 0) * 1.1;

    return {
      completed,
      scope,
      prediction,
      regression,
      maxX,
      maxY,
      totalDays,
      predictionDays,
      completionDay,
      currentCompleted: lastCompleted?.y || 0,
      totalScope: scopeHistory.total
    };
  }, [activityData, scopeHistory, loading, listId]);

  // Calculate stats
  const stats = useMemo(() => {
    if (!chartData.totalScope) return { progress: 0, pace: 0, remaining: 0, daysToComplete: null };

    const progress = chartData.totalScope > 0
      ? Math.round((chartData.currentCompleted / chartData.totalScope) * 100)
      : 0;

    const pace = chartData.totalDays > 0
      ? (chartData.currentCompleted / chartData.totalDays).toFixed(1)
      : 0;

    const remaining = Math.max(0, chartData.totalScope - chartData.currentCompleted);

    let daysToComplete = null;
    if (chartData.completionDay && chartData.completionDay > chartData.totalDays) {
      daysToComplete = chartData.completionDay - chartData.totalDays;
    }

    return { progress, pace, remaining, daysToComplete };
  }, [chartData]);

  // Generate paths
  const completedPath = useMemo(() =>
    generateSmoothPath(chartData.completed, chartWidth, chartHeight, chartData.maxX, chartData.maxY),
    [chartData.completed, chartWidth, chartHeight, chartData.maxX, chartData.maxY]
  );

  const completedAreaPath = useMemo(() =>
    generateAreaPath(chartData.completed, chartWidth, chartHeight, chartData.maxX, chartData.maxY),
    [chartData.completed, chartWidth, chartHeight, chartData.maxX, chartData.maxY]
  );

  const scopePath = useMemo(() =>
    generateSmoothPath(chartData.scope, chartWidth, chartHeight, chartData.maxX, chartData.maxY),
    [chartData.scope, chartWidth, chartHeight, chartData.maxX, chartData.maxY]
  );

  const predictionPath = useMemo(() =>
    generateSmoothPath(chartData.prediction, chartWidth, chartHeight, chartData.maxX, chartData.maxY),
    [chartData.prediction, chartWidth, chartHeight, chartData.maxX, chartData.maxY]
  );

  // X-axis labels
  const xLabels = useMemo(() => {
    if (!chartData.totalDays) return [];

    const labels = [];

    // Start label
    labels.push({ x: 0, label: t('prediction.start') });

    // Today label
    labels.push({ x: chartData.totalDays, label: t('prediction.now') });

    // Prediction end
    if (chartData.predictionDays > 0) {
      labels.push({ x: chartData.maxX, label: t('prediction.daysAhead', { count: chartData.predictionDays }) });
    }

    return labels;
  }, [chartData, t]);

  // Handle mouse move over chart for hover tooltip
  const handleChartHover = useCallback((e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const scaleX = svgWidth / rect.width;

    // Calculate position within chart area
    const mouseX = (e.clientX - rect.left) * scaleX - padding.left;

    if (mouseX < 0 || mouseX > chartWidth) {
      setHoverInfo(null);
      return;
    }

    // Convert x position to day index
    const dayIndex = Math.round((mouseX / chartWidth) * chartData.maxX);
    const clampedDay = Math.max(0, Math.min(dayIndex, chartData.maxX));

    // Get values from the data arrays
    const scopePoint = chartData.scope.find(p => p.x === clampedDay) ||
                       chartData.scope[chartData.scope.length - 1];
    const completedPoint = chartData.completed.find(p => p.x === clampedDay);
    const predictionPoint = chartData.prediction.find(p => p.x === clampedDay);

    // Calculate the actual date
    const currentList = listId && listId !== 'all' ? getListById(listId) : null;
    const listCreatedAt = currentList?.created_at ? new Date(currentList.created_at) : null;
    const startDate = listCreatedAt || new Date(Date.now() - 18 * 7 * MS_PER_DAY);
    const hoverDate = new Date(startDate);
    hoverDate.setDate(hoverDate.getDate() + clampedDay);

    setHoverInfo({
      dayIndex: clampedDay,
      x: (clampedDay / chartData.maxX) * chartWidth,
      scopeValue: scopePoint?.y || 0,
      completedValue: completedPoint?.y || (predictionPoint?.y || 0),
      isPrediction: clampedDay > chartData.totalDays,
      date: hoverDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    });
  }, [chartData, chartWidth, svgWidth, padding.left, listId]);

  const handleChartLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('prediction.titleWithList', { list: listName })}
      size="lg"
      className="prediction-modal"
    >
      {loading ? (
        <div className="prediction-loading">{t('prediction.loading')}</div>
      ) : (
        <div className="prediction-content">
          {/* SVG Chart */}
          <div className="prediction-chart">
            <svg
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              className="progress-svg"
              preserveAspectRatio="xMidYMid meet"
              onMouseMove={handleChartHover}
              onMouseLeave={handleChartLeave}
            >
              <defs>
                {/* Gradient for completed area */}
                <linearGradient id="completedGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="var(--chart-completed, #3b82f6)" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="var(--chart-completed, #3b82f6)" stopOpacity="0.05" />
                </linearGradient>

                {/* Gradient for prediction area */}
                <linearGradient id="predictionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="var(--chart-completed, #3b82f6)" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="var(--chart-completed, #3b82f6)" stopOpacity="0.02" />
                </linearGradient>
              </defs>

              <g transform={`translate(${padding.left}, ${padding.top})`}>
                {/* Grid lines */}
                <g className="grid-lines">
                  {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                    <line
                      key={i}
                      x1={0}
                      y1={chartHeight * (1 - ratio)}
                      x2={chartWidth}
                      y2={chartHeight * (1 - ratio)}
                      className="grid-line"
                    />
                  ))}
                </g>

                {/* Completed area (gradient fill) */}
                {completedAreaPath && (
                  <path
                    d={completedAreaPath}
                    fill="url(#completedGradient)"
                    className="completed-area"
                  />
                )}

                {/* Scope line (gray, dashed) */}
                {scopePath && (
                  <path
                    d={scopePath}
                    fill="none"
                    className="scope-line"
                  />
                )}

                {/* Completed line (blue, solid) */}
                {completedPath && (
                  <path
                    d={completedPath}
                    fill="none"
                    className="completed-line"
                  />
                )}

                {/* Prediction line (blue, dashed) */}
                {predictionPath && (
                  <path
                    d={predictionPath}
                    fill="none"
                    className="prediction-line"
                  />
                )}

                {/* "Now" vertical line */}
                {chartData.totalDays > 0 && (
                  <line
                    x1={(chartData.totalDays / chartData.maxX) * chartWidth}
                    y1={0}
                    x2={(chartData.totalDays / chartData.maxX) * chartWidth}
                    y2={chartHeight}
                    className="now-line"
                  />
                )}

                {/* Y-axis labels */}
                <g className="y-axis-labels">
                  <text x={-8} y={chartHeight} className="axis-label" textAnchor="end">0</text>
                  <text x={-8} y={chartHeight / 2} className="axis-label" textAnchor="end">
                    {Math.round(chartData.maxY / 2)}
                  </text>
                  <text x={-8} y={4} className="axis-label" textAnchor="end">
                    {Math.round(chartData.maxY)}
                  </text>
                </g>

                {/* X-axis labels */}
                <g className="x-axis-labels">
                  {xLabels.map((label, i) => (
                    <text
                      key={i}
                      x={(label.x / chartData.maxX) * chartWidth}
                      y={chartHeight + 20}
                      className={`axis-label ${label.label.startsWith('+') ? 'prediction-label' : ''}`}
                      textAnchor="middle"
                    >
                      {label.label}
                    </text>
                  ))}
                </g>

                {/* Hover vertical line and tooltip */}
                {hoverInfo && (
                  <g className="hover-group">
                    {/* Vertical line */}
                    <line
                      x1={hoverInfo.x}
                      y1={0}
                      x2={hoverInfo.x}
                      y2={chartHeight}
                      className="hover-line"
                    />

                    {/* Data points */}
                    <circle
                      cx={hoverInfo.x}
                      cy={chartHeight - (hoverInfo.scopeValue / chartData.maxY) * chartHeight}
                      r={4}
                      className="hover-dot scope-dot"
                    />
                    <circle
                      cx={hoverInfo.x}
                      cy={chartHeight - (hoverInfo.completedValue / chartData.maxY) * chartHeight}
                      r={4}
                      className={`hover-dot ${hoverInfo.isPrediction ? 'prediction-dot' : 'completed-dot'}`}
                    />

                    {/* Tooltip */}
                    <g transform={`translate(${hoverInfo.x}, -5)`}>
                      <rect
                        x={hoverInfo.x > chartWidth / 2 ? -110 : 10}
                        y={0}
                        width={100}
                        height={54}
                        rx={4}
                        className="hover-tooltip-bg"
                      />
                      <text
                        x={hoverInfo.x > chartWidth / 2 ? -60 : 60}
                        y={14}
                        textAnchor="middle"
                        className="hover-tooltip-date"
                      >
                        {hoverInfo.date}{hoverInfo.isPrediction ? ' (proj)' : ''}
                      </text>
                      <text
                        x={hoverInfo.x > chartWidth / 2 ? -60 : 60}
                        y={30}
                        textAnchor="middle"
                        className="hover-tooltip-value scope-value"
                      >
                        Scope: {hoverInfo.scopeValue}
                      </text>
                      <text
                        x={hoverInfo.x > chartWidth / 2 ? -60 : 60}
                        y={46}
                        textAnchor="middle"
                        className={`hover-tooltip-value ${hoverInfo.isPrediction ? 'prediction-value' : 'completed-value'}`}
                      >
                        {hoverInfo.isPrediction ? t('prediction.projected') : t('prediction.done')}: {Math.round(hoverInfo.completedValue)}
                      </text>
                    </g>
                  </g>
                )}
              </g>
            </svg>
          </div>

          {/* Legend */}
          <div className="chart-legend">
            <span className="legend-item">
              <span className="legend-line scope"></span> {t('prediction.scope')}
            </span>
            <span className="legend-item">
              <span className="legend-line completed"></span> {t('prediction.completed')}
            </span>
            <span className="legend-item">
              <span className="legend-line prediction"></span> {t('prediction.projected')}
            </span>
          </div>

          {/* Stats Grid */}
          <div className="prediction-stats">
            <div className="stat-card">
              <span className="stat-value">{stats.progress}%</span>
              <span className="stat-label">{t('prediction.progress')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{chartData.currentCompleted}</span>
              <span className="stat-label">{t('prediction.completed')}</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.remaining}</span>
              <span className="stat-label">{t('prediction.remaining')}</span>
            </div>
          </div>

          {/* Additional Info */}
          <div className="prediction-info">
            <div className="info-row">
              <span className="info-label">{t('prediction.totalScope')}</span>
              <span className="info-value">{t('prediction.blocksCount', { count: chartData.totalScope })}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('prediction.dailyPace')}</span>
              <span className="info-value">{t('prediction.blocksPerDay', { pace: stats.pace })}</span>
            </div>
            {chartData.regression && chartData.regression.r2 > 0 && (
              <div className="info-row">
                <span className="info-label">{t('prediction.trendFit')}</span>
                <span className="info-value">{(chartData.regression.r2 * 100).toFixed(0)}%</span>
              </div>
            )}
            {stats.daysToComplete && (
              <div className="info-row highlight">
                <span className="info-label">{t('prediction.estCompletion')}</span>
                <span className="info-value">{t('prediction.daysCount', { count: stats.daysToComplete })}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default PredictionModal;
